import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Avatar from "@/components/Avatar";
import { ArrowLeft, Send } from "lucide-react";
import { toast } from "sonner";

function formatStamp(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay
    ? d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
    : d.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function MessageThread() {
  const { userId } = useParams();
  const { user } = useAuth();
  const [other, setOther] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const scrollerRef = useRef(null);
  const composerRef = useRef(null);

  const fetchThread = async () => {
    try {
      const { data } = await api.get(`/messages/thread/${userId}`);
      setOther(data.other);
      setMessages(data.messages || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load conversation");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchThread();
    const id = setInterval(fetchThread, 8_000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages.length]);

  const send = async (e) => {
    e?.preventDefault();
    const content = draft.trim();
    if (!content || busy) return;
    setBusy(true);
    // Optimistic insert
    const optimistic = {
      id: `local-${Date.now()}`,
      sender_id: user?.id,
      content,
      created_at: new Date().toISOString(),
      mine: true,
      pending: true,
    };
    setMessages((m) => [...m, optimistic]);
    setDraft("");
    try {
      const { data } = await api.post("/messages/send", { recipient_id: userId, content });
      setMessages((m) => m.map((x) => (x.id === optimistic.id ? { ...data, mine: true } : x)));
    } catch (err) {
      setMessages((m) => m.filter((x) => x.id !== optimistic.id));
      setDraft(content);
      toast.error(err?.response?.data?.detail || "Could not send");
    } finally {
      setBusy(false);
      composerRef.current?.focus();
    }
  };

  if (loading) return <div className="max-w-3xl mx-auto px-6 py-16 text-center text-[var(--jj-muted)]">Loading…</div>;
  if (!other) return null;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
      {/* Header */}
      <div className="flex items-center gap-3 pb-4 border-b border-jj">
        <Link to="/messages" className="text-jjblue/70 hover:text-burgundy" data-testid="thread-back">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div className="h-10 w-10 shrink-0">
          <Avatar user={other} size={40} testId="thread-other-avatar" />
        </div>
        <div className="min-w-0">
          <div className="font-serif text-xl text-jjblue leading-tight truncate" data-testid="thread-other-name">{other.name}</div>
          <div className="text-[11px] uppercase tracking-wider text-[var(--jj-muted)]">{other.tier}</div>
        </div>
      </div>

      {/* Messages */}
      <div
        ref={scrollerRef}
        className="mt-4 jj-card !p-0 overflow-y-auto"
        style={{ height: "calc(100vh - 22rem)", minHeight: "20rem" }}
        data-testid="thread-scroller"
      >
        {messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center px-8">
            <div className="h-12 w-12 rounded-full bg-cream-surface flex items-center justify-center">
              <Send className="h-5 w-5 text-burgundy" strokeWidth={1.5} />
            </div>
            <h3 className="font-serif text-xl text-jjblue mt-4">Say hello to {other.name.split(" ")[0]}.</h3>
            <p className="mt-2 text-sm text-[var(--jj-muted)] max-w-md">Be encouraging. Be specific. The Lord uses ordinary words.</p>
          </div>
        ) : (
          <div className="p-5 space-y-3">
            {messages.map((m, i) => {
              const prev = messages[i - 1];
              const showStamp = !prev || (new Date(m.created_at) - new Date(prev.created_at)) > 10 * 60 * 1000;
              return (
                <div key={m.id} data-testid={`msg-${m.id}`}>
                  {showStamp && (
                    <div className="text-center text-[11px] uppercase tracking-wider text-[var(--jj-muted)] py-2">
                      {formatStamp(m.created_at)}
                    </div>
                  )}
                  <div className={`flex ${m.mine ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`max-w-[78%] px-4 py-2.5 text-[15px] leading-relaxed whitespace-pre-wrap break-words ${
                        m.mine
                          ? `bg-burgundy text-cream rounded-2xl rounded-br-sm ${m.pending ? "opacity-70" : ""}`
                          : "bg-cream-surface text-jjblue rounded-2xl rounded-bl-sm border border-jj"
                      }`}
                    >
                      {m.content}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Composer */}
      <form onSubmit={send} className="mt-3 flex items-end gap-2">
        <textarea
          ref={composerRef}
          rows={1}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send(e);
            }
          }}
          placeholder="Write to a brother or sister…"
          className="input-jj !py-3 resize-none flex-1"
          style={{ maxHeight: "8rem" }}
          data-testid="thread-composer"
        />
        <button
          type="submit"
          disabled={busy || !draft.trim()}
          className="btn-primary !py-3 !px-5 flex items-center gap-1.5 disabled:opacity-40"
          data-testid="thread-send"
        >
          <Send className="h-4 w-4" /> Send
        </button>
      </form>
    </div>
  );
}
